/**
 * The default words for every rule effect (03 §2, 10 §3): the standing order, the countdown and the
 * banner. One table per question, each total over its effect type, so a new effect without words is
 * a compile error. A rule may carry its own (`orders`, `warning`, `reason`: `RuleText` in
 * src/sim/types/objective.ts); these are what the player reads when it does not. Never keyed by archetype.
 */

import type { AlertEffect, MissionDef, ObjectiveRule, TimedEffect } from '../../sim/types';

export type AlertRule = Extract<ObjectiveRule, { type: 'alertConsequence' }>;
export type TimedRule = Extract<ObjectiveRule, { type: 'timedEvent' }>;

export const mmss = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** The originals' standing orders, exactly as first written. Null: the effect cannot lose the mission. */
export const ALERT_ORDERS: Record<AlertEffect, string | null> = {
  jetsScramble: 'NO NOISE UNTIL YOU ARE INSIDE THE WIRE, OR THE JETS SCRAMBLE',
  prisonersMoved: 'AN ALARM BEFORE THE CELLS ARE OPEN AND THE PRISONERS ARE MOVED',
  demolition: 'THE PRISON IS WIRED: AN ALARM BEFORE THE DOOR IS OPEN BLOWS IT',
  officerFlees: 'TAKE HIM ALIVE: AT FULL ALARM HE RUNS',
  reinforceTarget: null,
  targetsLeave: 'IF THE GARRISON IS ALERTED THE TARGETS DRIVE OFF',
  documentsBurned: 'IF THE GARRISON IS ALERTED THEY BURN WHAT YOU CAME FOR',
  contactAborts: 'IF THE GARRISON IS ALERTED BEFORE THE MEETING, THE CONTACT STAYS AWAY',
  targetFlees: 'IF THE GARRISON IS ALERTED HE IS TAKEN AWAY',
  shootTheCaptive: 'AT FULL ALARM THE GUARDS SHOOT HIM RATHER THAN LOSE HIM',
};

/**
 * Can this consequence LOSE the mission? The collapsed standing orders (missionOrders.ts) keep one line, and it
 * must be one of these. Total over the effects, so a new effect has to answer the question.
 */
export const ALERT_LOSES: Record<AlertEffect, boolean> = {
  jetsScramble: true,
  prisonersMoved: true,
  demolition: true,
  officerFlees: true,
  // More men on the target is a harder fight, never a lost mission.
  reinforceTarget: false,
  targetsLeave: true,
  documentsBurned: true,
  contactAborts: true,
  targetFlees: true,
  shootTheCaptive: true,
};

/**
 * Can this timed event lose the mission? `convoyCrosses` and `searchPartyArrives` both depend on the mission:
 * a convoy is a deadline only when the rule says the job must be done first (or the window IS the job), and a
 * search party only loses it when there is somebody for it to find before the ranger does.
 */
export function timedLoses(def: MissionDef, rule: TimedRule): boolean {
  switch (rule.effect) {
    case 'targetsLeave':
    case 'strikeLands':
      return true;
    case 'convoyCrosses':
      return rule.failIfIncomplete === true || def.objective.duringWindow === true;
    case 'searchPartyArrives':
      return def.captives.length > 0;
    // The contact and the Osprey arrive on their own time; a found cell is an alarm, not a loss.
    case 'contactArrives':
    case 'cellFoundEmpty':
    case 'ospreyArrives':
      return false;
    default:
      return false;
  }
}

/** What is counting down once the alert has tripped a consequence. Null: nothing to show (it is not a loss). */
export const ALERT_COUNTDOWN: Record<AlertEffect, string | null> = {
  jetsScramble: 'ENGINES STARTING — JETS SCRAMBLE IN',
  prisonersMoved: 'THEY ARE MOVING THE PRISONERS IN',
  demolition: 'THE PRISON BLOWS IN',
  officerFlees: null,
  reinforceTarget: null,
  targetsLeave: 'ENGINES RUNNING — THE TARGETS LEAVE IN',
  documentsBurned: 'THEY ARE BURNING IT — GONE IN',
  contactAborts: 'THE CONTACT IS PULLING OUT IN',
  targetFlees: 'HE IS BEING HURRIED AWAY — GONE IN',
  shootTheCaptive: 'THE ORDER TO SHOOT HIM GOES OUT IN',
};

/** "INSIDE" reads better than a rectangle: every `unlessInsideZone` is the place he is trying to get into. */
export function alertOrders(rule: AlertRule): string | null {
  if (rule.orders !== undefined) return rule.orders;
  const text = ALERT_ORDERS[rule.effect];
  return text === null || rule.unlessInsideZone === undefined ? text : `${text} — UNTIL YOU ARE INSIDE`;
}

const hastens = (rule: TimedRule): string => (rule.hastenAtAlert === undefined ? '' : ' — AN ALARM BRINGS IT FORWARD');

/** The standing order for a timed event. Null: it cannot lose the mission and needs no order (its clock still shows). */
export const TIMED_ORDERS: Record<TimedEffect, (def: MissionDef, rule: TimedRule) => string | null> = {
  targetsLeave: (_def, rule) => `THE TARGETS LEAVE ON THE CLOCK: DESTROY THEM FIRST${hastens(rule)}`,
  convoyCrosses: (def, rule) => {
    if (def.objective.duringWindow === true) return 'BE IN POSITION WHILE THE CONVOY PASSES: ARRIVE LATE AND THE MISSION IS LOST';
    return rule.failIfIncomplete === true ? `THE JOB MUST BE DONE BEFORE THE COLUMN COMES${hastens(rule)}` : null;
  },
  searchPartyArrives: (def, rule) => (def.captives.length > 0
    ? `A SEARCH PARTY IS COMING FOR HIM: FIND HIM FIRST${hastens(rule)}` : null),
  contactArrives: () => 'THE CONTACT COMES TO THE RENDEZVOUS AT THE STATED TIME, NOT BEFORE',
  strikeLands: (def, rule) => {
    const clear = def.objective.clearDistance ?? 0;
    const when = rule.from === 'armed' ? `${mmss(rule.atSeconds)} AFTER YOU PLACE IT`
      : rule.from === 'complete' ? `${mmss(rule.atSeconds)} AFTER THE LAST MARK` : 'ON THE CLOCK';
    return `THE STRIKE LANDS ${when}: BE ${clear} M CLEAR OR DIE IN IT`;
  },
  cellFoundEmpty: () => 'THE ALARM GOES UP WHEN THEY FIND THE CELL EMPTY',
  ospreyArrives: () => 'THE OSPREY COMES ON ITS OWN WHEN THE JOB IS DONE',
};

/** The countdown's words while a timed event is pending. */
export const TIMED_COUNTDOWN: Record<TimedEffect, string> = {
  targetsLeave: 'THE TARGETS LEAVE IN',
  convoyCrosses: 'CONVOY DUE IN',
  searchPartyArrives: 'SEARCH PARTY DUE IN',
  contactArrives: 'CONTACT DUE IN',
  strikeLands: 'STRIKE LANDS IN',
  cellFoundEmpty: 'CELL FOUND EMPTY IN',
  ospreyArrives: 'OSPREY DUE IN',
};

/** While an event's window is open: what is happening, before "m:ss LEFT". Only a convoy has one today. */
export const TIMED_WINDOW: Record<TimedEffect, string> = {
  targetsLeave: 'THEY ARE LEAVING',
  convoyCrosses: 'THE CONVOY IS PASSING',
  searchPartyArrives: 'THE SEARCH PARTY IS SWEEPING',
  contactArrives: 'THE CONTACT IS WAITING',
  strikeLands: 'THE STRIKE IS COMING IN',
  cellFoundEmpty: 'THEY ARE SEARCHING THE CELLS',
  ospreyArrives: 'THE OSPREY IS COMING',
};

/** The banner when it happens. Null: something else already says it (the failure, the Osprey, the blast). */
export const TIMED_BANNER: Record<TimedEffect, string | null> = {
  targetsLeave: null,
  convoyCrosses: 'THE CONVOY IS ON THE ROAD',
  searchPartyArrives: 'ENEMY SEARCH PARTY IN THE AREA',
  contactArrives: 'THE CONTACT IS AT THE RENDEZVOUS',
  strikeLands: 'THE STRIKE IS IN',
  cellFoundEmpty: 'THEY HAVE FOUND THE CELL EMPTY',
  ospreyArrives: null,
};
